import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Emoji } from './Emoji';
import { REACTIONS } from './emoji-list';
import { cn } from './util';

export interface ReactionBarProps {
  onReact: (emoji: string) => void;
  /** Client-side cooldown between reactions (ms). */
  cooldownMs?: number;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The 6 reaction buttons (😂 🔥 😱 👏 🤑 🍅). After a reaction all buttons cool down with a
 * clock-wipe overlay. Number keys are left to the caller (avoid clashing with bid inputs).
 */
export function ReactionBar({
  onReact,
  cooldownMs = 1500,
  disabled,
  size = 'md',
  className,
}: ReactionBarProps) {
  const [cooling, setCooling] = useState(0); // increments per cooldown so the wipe restarts
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!cooling) return;
    const t = setTimeout(() => setActive(false), cooldownMs);
    return () => clearTimeout(t);
  }, [cooling, cooldownMs]);

  const fire = (emoji: string) => {
    if (disabled || active) return;
    onReact(emoji);
    setActive(true);
    setCooling((c) => c + 1);
  };

  const box = size === 'sm' ? 'size-9' : 'size-11';
  return (
    <div
      role="group"
      aria-label="Tepkiler"
      className={cn('inline-flex items-center gap-1.5', className)}
    >
      {REACTIONS.map((r) => (
        <motion.button
          key={r.emoji}
          type="button"
          aria-label={r.label}
          title={r.label}
          aria-disabled={disabled || active || undefined}
          // A mouse click must not pull keyboard focus off the bid field / score slider: the buyer
          // who reacts mid-turn and then hits Enter should bid, not send the emoji again.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => fire(r.emoji)}
          whileHover={disabled || active ? undefined : { y: -4, rotate: -8, scale: 1.12 }}
          whileTap={disabled || active ? undefined : { scale: 0.8, rotate: 10 }}
          transition={{ type: 'spring', stiffness: 600, damping: 15 }}
          className={cn(
            'relative flex items-center justify-center overflow-hidden rounded-full border-[2.5px] border-ink bg-white shadow-ink-xs',
            box,
            (disabled || active) && 'cursor-not-allowed',
            disabled && 'opacity-50 grayscale',
          )}
        >
          <Emoji emoji={r.emoji} label="" size={size === 'sm' ? 20 : 25} />
          {active && (
            <span
              key={cooling}
              aria-hidden
              className="animate-cooldown absolute inset-0"
              style={{
                ['--cooldown' as string]: `${cooldownMs}ms`,
                background:
                  'conic-gradient(rgb(17 17 17 / 0.42) var(--cooldown-angle), transparent 0)',
              }}
            />
          )}
        </motion.button>
      ))}
    </div>
  );
}
